import React, { ButtonHTMLAttributes, forwardRef } from "react";
import { cn } from "@/lib/utils";

export interface ThemedButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "outline" | "ghost";
  size?: "sm" | "md" | "lg" | "xl";
  /** Custom color class for background (e.g., "bg-pink-400", "bg-green-400") */
  colorClass?: string;
}

export const ThemedButton = forwardRef<HTMLButtonElement, ThemedButtonProps>(
  ({ variant = "primary", size = "md", colorClass, className, children, ...props }, ref) => {
    return (
      <button
        ref={ref}
        className={cn(
          // Base styles
          "inline-flex items-center justify-center transition-all",
          // Theme-aware styles
          "border-theme rounded-theme shadow-theme",
          "hover:shadow-theme-hover",
          // Font weight from theme
          "[font-weight:var(--theme-font-weight-button)]",
          // Variant styles
          variant === "primary" && [
            colorClass || "bg-theme-primary",
            "text-theme-button",
          ],
          variant === "secondary" && [
            colorClass || "bg-theme-secondary",
            "text-white",
          ],
          variant === "outline" && [
            "bg-white",
            "text-black",
            "hover:bg-gray-100",
          ],
          variant === "ghost" && [
            "bg-transparent",
            "shadow-none",
            "hover:bg-gray-100",
            "hover:shadow-none",
          ],
          // Size styles
          size === "sm" && "px-3 py-1.5 text-sm gap-1",
          size === "md" && "px-4 py-2 text-sm gap-1.5",
          size === "lg" && "px-6 py-3 text-base gap-2",
          size === "xl" && "px-8 py-4 text-lg gap-2",
          // Disabled styles
          "disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none",
          className
        )}
        {...props}
      >
        {children}
      </button>
    );
  }
);

ThemedButton.displayName = "ThemedButton";
